import React from 'react'
import {Badge, Button, Label} from '@therms/atalaya'
import {
  arrayValuesOrUndefined,
  ErrorBoundary,
  RPCDocs,
  useLocationContext,
} from '@therms/react-modules'
import {useCurrentEventCounts} from '@src/modules/Events/hooks/useCurrentEventCounts'
import {ActiveEventsList} from '@src/modules/Events/components/ActiveEventsList'
import {PendingEventsList} from '@src/modules/Events/components/PendingEventsList'
import {RecentlyClosedEventsList} from '@src/modules/Events/components/RecentlyClosedEventsList'

interface EventsColumnsLayoutProps {
  activityTypeIds?: string[]
  dateTimeRange: {start?: string; end?: string}
  eventTypeIds?: string[]
  locationId?: string
  onClickViewAllClosed?: () => void
  onSelectEventId: (eventId: string) => void
  searchText?: string
  userIdsAssigned?: string[]
}

function EventsColumnsLayout({
  activityTypeIds,
  dateTimeRange,
  eventTypeIds,
  locationId,
  onClickViewAllClosed,
  onSelectEventId,
  searchText,
  userIdsAssigned,
}: EventsColumnsLayoutProps) {
  const locationCtx = useLocationContext()

  const {counts} = useCurrentEventCounts({
    activityTypeIdFilter: arrayValuesOrUndefined(activityTypeIds),
    assignedUserIdFilter: arrayValuesOrUndefined(userIdsAssigned),
    eventTypeIds: arrayValuesOrUndefined(eventTypeIds),
    fromDate: dateTimeRange.start,
    locationId: locationCtx?.locationId ?? locationId,
    toDate: dateTimeRange.end,
  })

  const handleEventClicked = (event: RPCDocs.Events.GetEventsList.v1.Event) => {
    onSelectEventId(event.id)
  }

  return (
    <div className="flex">
      <div className="grow">
        <div className="m-xs">
          <div className="mb-sm">
            <div className="gap-x-sm inline-flex">
              <Label>Pending</Label>

              <Badge value={counts.pending} variant="caution" />
            </div>
          </div>

          <PendingEventsList
            activityTypeIds={activityTypeIds}
            eventTypeIds={eventTypeIds}
            dateTimeRange={dateTimeRange}
            locationId={locationId}
            onClickEvent={handleEventClicked}
            searchText={searchText}
            userIdsAssigned={userIdsAssigned}
          />
        </div>
      </div>

      <div className="grow">
        <div className="m-xs">
          <div className="mb-sm">
            <div className="gap-x-sm inline-flex">
              <Label>Active</Label>

              <Badge value={counts.active} variant="main" />
            </div>
          </div>

          <ErrorBoundary>
            <ActiveEventsList
              activityTypeIds={activityTypeIds}
              eventTypeIds={eventTypeIds}
              dateTimeRange={dateTimeRange}
              locationId={locationId}
              onClickEvent={handleEventClicked}
              searchText={searchText}
              userIdsAssigned={userIdsAssigned}
            />
          </ErrorBoundary>
        </div>
      </div>

      <div className="grow">
        <div className="m-xs">
          <div className="mb-sm">
            <div className="gap-x-sm inline-flex w-full items-center justify-between">
              <Label>Recently Closed</Label>

              <Button
                size="xs"
                subtle
                onClick={onClickViewAllClosed}
                variant="main"
              >
                <div className="px-xxs">View All</div>
              </Button>
            </div>
          </div>

          <ErrorBoundary>
            <RecentlyClosedEventsList
              activityTypeIds={activityTypeIds}
              eventTypeIds={eventTypeIds}
              dateTimeRange={dateTimeRange}
              locationId={locationId}
              onClickEvent={handleEventClicked}
              searchText={searchText}
              userIdsAssigned={userIdsAssigned}
            />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}

export {EventsColumnsLayout}
